import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
  fetchTaxesRequest,
  fetchTaxesSuccess,
  fetchTaxesFailure,
  searchTaxesRequest,
  searchTaxesSuccess,
  searchTaxesFailure,
  fetchTaxDetailsRequest,
  fetchTaxDetailsSuccess,
  fetchTaxDetailsFailure,
  createTaxRequest,
  createTaxSuccess,
  createTaxFailure,
  updateTaxRequest,
  updateTaxSuccess,
  updateTaxFailure,
  deleteTaxRequest,
  deleteTaxSuccess,
  deleteTaxFailure,
} from '../slices/tax.slice';

import {
  getTaxes,
  getTaxById,
  createTax,
  updateTax,
  deleteTax,
  searchTaxes,
} from '@/services/taxService';

import { 
  CreateTaxDto,
  UpdateTaxDto,
  FetchTaxesOptions 
} from '@/types/models/tax';
import { toApiError } from '@/lib/errorHelpers';

// Fetch taxes saga
function* fetchTaxesSaga(action: PayloadAction<FetchTaxesOptions>): Generator<any, void, any> {
  try {
    const response: any = yield call(getTaxes, action.payload);
    yield put(fetchTaxesSuccess({
      data: response.data || response,
      pagination: response.pagination || {
        page: action.payload.page || 1,
        total: response.length || 0,
        pageSize: action.payload.page_size || 10,
        totalPages: Math.ceil((response.length || 0) / (action.payload.page_size || 10)),
      }
    }));
  } catch (error: any) {
    yield put(fetchTaxesFailure(toApiError(error)));
  }
}

// Search taxes saga
function* searchTaxesSaga(action: PayloadAction<{ query: string; options?: FetchTaxesOptions }>): Generator<any, void, any> {
  try {
    const { query, options = {} } = action.payload;
    const response: any = yield call(searchTaxes, query, options);
    yield put(searchTaxesSuccess({
      data: response.data || response,
      pagination: response.pagination || {
        page: options.page || 1,
        total: response.length || 0,
        pageSize: options.page_size || 10,
        totalPages: Math.ceil((response.length || 0) / (options.page_size || 10)),
      }
    }));
  } catch (error: any) {
    yield put(searchTaxesFailure(toApiError(error)));
  }
}

// Fetch tax details saga
function* fetchTaxDetailsSaga(action: PayloadAction<{ id: string }>): Generator<any, void, any> {
  try {
    const response: any = yield call(getTaxById, action.payload.id);
    yield put(fetchTaxDetailsSuccess(response.data));
  } catch (error: any) {
    yield put(fetchTaxDetailsFailure(toApiError(error)));
  }
}

// Create tax saga
function* createTaxSaga(action: PayloadAction<CreateTaxDto>): Generator<any, void, any> {
  try {
    const response: any = yield call(createTax as any, action.payload);
    yield put(createTaxSuccess(response.data));
  } catch (error: any) {
    yield put(createTaxFailure(toApiError(error)));
  }
}

// Update tax saga
function* updateTaxSaga(action: PayloadAction<{ id: string; data: UpdateTaxDto }>): Generator<any, void, any> {
  try {
    const { id, data } = action.payload;
    const response: any = yield call(updateTax as any, id, data);
    yield put(updateTaxSuccess(response.data));
  } catch (error: any) {
    yield put(updateTaxFailure(toApiError(error)));
  }
}

// Delete tax saga
function* deleteTaxSaga(action: PayloadAction<{ id: string }>): Generator<any, void, any> {
  try {
    yield call(deleteTax, action.payload.id);
    yield put(deleteTaxSuccess({ id: action.payload.id }));
  } catch (error: any) {
  yield put(deleteTaxFailure(toApiError(error)));
  }
}

// Root tax saga
export function* taxSaga(): Generator<any, void, any> {
  yield takeLatest(fetchTaxesRequest.type, fetchTaxesSaga);
  yield takeLatest(searchTaxesRequest.type, searchTaxesSaga);
  yield takeLatest(fetchTaxDetailsRequest.type, fetchTaxDetailsSaga);
  yield takeLatest(createTaxRequest.type, createTaxSaga);
  yield takeLatest(updateTaxRequest.type, updateTaxSaga);
  yield takeLatest(deleteTaxRequest.type, deleteTaxSaga);
}

export default taxSaga;
